import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { isEqual } from "lodash"

import Icon from "@/components/icon/Icon"
import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import SeasonWeekRow from "@/app/settings/analysis-groups/season/SeasonWeekRow"
import {
  useCreateSeasonWeekMutation,
  useMatchSeasonWeekActivitiesMutation,
  useSeasonWeekQuery,
} from "@/hooks/api/useSettingQuery"
import { SeasonWeek } from "@/types/setting"
import { createGlobalStore } from "@/stores/global"
import { observeMuiErrors } from "@/utils/error"
import { DEFAULT_COVERAGE_OPTIONS } from "@/app/settings/analysis-groups/season/constants"

import "./seasonWeek.scss"

let newId = 1

export default function SeasonWeekPage() {
  const { t } = useTranslation()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const [newSeasonWeeks, setNewSeasonWeeks] = useState<SeasonWeek[]>([])
  const [deleteRowOpen, setDeleteRowOpen] = useState(false)
  const [deleteNewId, setDeleteNewId] = useState<number | null>(null)
  const [matchDialogOpen, setMatchDialogOpen] = useState(false)
  const [matchIdfSeasonWeek, setMatchIdfSeasonWeek] = useState<number | null>(null)
  const [datePickerErr, setDatePickerErr] = useState(false)

  const { data: seasonWeeks } = useSeasonWeekQuery()
  const { mutateAsync: createSeasonWeeks } = useCreateSeasonWeekMutation()
  const { mutateAsync: matchActivities } = useMatchSeasonWeekActivitiesMutation()

  useEffect(() => {
    if (seasonWeeks) {
      setNewSeasonWeeks(seasonWeeks.seasonWeeks.map((item) => ({ ...item, newId: newId++ })))
    }
  }, [seasonWeeks])

  // 페이지 내의 모든 text field 에러는 해당 옵저버로 확인
  useEffect(() => {
    const observer = observeMuiErrors((hasErr) => setDatePickerErr(hasErr))

    return () => {
      observer.disconnect()
    }
  }, [])

  const onAddSeasonWeek = () => {
    // 새 시즌위크 추가 / 삭제를 위한 임시 id 추가
    setNewSeasonWeeks([
      {
        idfSeasonWeek: null,
        name: "",
        active: true,
        startDate: null,
        endDate: null,
        coverage: DEFAULT_COVERAGE_OPTIONS.map((item) => item.value),
        newId: newId++,
      },
      ...newSeasonWeeks,
    ])
  }
  const isDisabledSaveBtn = useMemo(() => {
    const isEmpty = seasonWeeks?.seasonWeeks.length === 0 && newSeasonWeeks.length === 0
    const isOver10 = newSeasonWeeks.length > 10
    const isEmptyName = newSeasonWeeks.some((item) => item.active && !item.name?.trim())
    const isEmptyCoverage = newSeasonWeeks.some((item) => item.coverage.length === 0)
    const invalidDate = newSeasonWeeks.some(
      (item) => !item.startDate || !item.endDate || item.startDate > item.endDate,
    )

    return (
      isEqual(
        seasonWeeks?.seasonWeeks,
        newSeasonWeeks.map((sw) => ({
          idfSeasonWeek: sw.idfSeasonWeek,
          name: sw.name,
          active: sw.active,
          startDate: sw.startDate,
          endDate: sw.endDate,
          coverage: sw.coverage,
        })),
      ) ||
      isEmpty ||
      isOver10 ||
      isEmptyName ||
      isEmptyCoverage ||
      invalidDate ||
      datePickerErr
    )
  }, [seasonWeeks, newSeasonWeeks, datePickerErr])

  const onOpenMatchDialog = (idfSeasonWeek: number) => {
    setMatchIdfSeasonWeek(idfSeasonWeek)
    setMatchDialogOpen(true)
  }

  const onMatchActivities = async () => {
    if (!matchIdfSeasonWeek) return
    try {
      await matchActivities(matchIdfSeasonWeek)
      setSnackBar({ message: t("messages.successUpdate"), severity: "success", open: true })
    } catch (err) {
      return err
    } finally {
      setMatchDialogOpen(false)
      setMatchIdfSeasonWeek(null)
    }
  }

  const onSave = async () => {
    try {
      await createSeasonWeeks({
        seasonWeeks: newSeasonWeeks.map((sw) => ({
          idfSeasonWeek: sw.idfSeasonWeek,
          name: sw.name,
          active: sw.active,
          startDate: sw.startDate,
          endDate: sw.endDate,
          coverage: sw.coverage,
        })),
      })
      setSnackBar({ message: t("messages.successUpdate"), severity: "success", open: true })
    } catch (err) {
      return err
    }
  }

  return (
    <div className="SeasonWeekCont">
      <div className="NewSeasonWeekButtonCont">
        <h3 className="settings-section-title">{t("season")}</h3>
        <BasicButton
          type="default-outlined"
          size="sm"
          onClick={onAddSeasonWeek}
          disabled={newSeasonWeeks.length === 10}>
          <span className="season-week-button-content">
            <Icon type="add" width={16} height={16} />
            {t("button.newSeason")}
          </span>
        </BasicButton>
      </div>

      <div className="SeasonWeekContentCont">
        {!newSeasonWeeks.length ? (
          <div className="EmptySeasonWeekCont">
            <p className="empty-season-week-text">{t("messages.noDataAvailable")}</p>
          </div>
        ) : (
          newSeasonWeeks.map((seasonWeek, index) => (
            <SeasonWeekRow
              key={seasonWeek.newId}
              item={seasonWeek}
              seasonWeeks={newSeasonWeeks}
              onChangeSeasonWeeks={(seasonWeeks) => setNewSeasonWeeks(seasonWeeks)}
              openDialog={() => {
                if (seasonWeek.newId) {
                  setDeleteNewId(Number(seasonWeek.newId))
                  setDeleteRowOpen(true)
                }
              }}
              onMatchActivities={onOpenMatchDialog}
            />
          ))
        )}
      </div>

      <div className="SeasonWeekButtonCont">
        <BasicButton
          type="primary-contained"
          size="lg"
          onClick={onSave}
          disabled={isDisabledSaveBtn}>
          {t("button.save")}
        </BasicButton>
      </div>

      <Dialog
        open={deleteRowOpen}
        title={t("confirm")}
        onClose={() => setDeleteRowOpen(false)}
        maxWidth="400px"
        footer={
          <>
            <BasicButton type="default-outlined" size="md" onClick={() => setDeleteRowOpen(false)}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton
              type="danger-outlined"
              size="md"
              onClick={() => {
                setNewSeasonWeeks(newSeasonWeeks.filter((item) => item.newId !== deleteNewId))
                setDeleteNewId(null)
                setDeleteRowOpen(false)
              }}>
              {t("button.delete")}
            </BasicButton>
          </>
        }>
        <p className="confirm-delete-text">{t("messages.confirmDelete")}</p>
      </Dialog>

      <Dialog
        open={matchDialogOpen}
        title={t("confirm")}
        onClose={() => {
          setMatchDialogOpen(false)
          setMatchIdfSeasonWeek(null)
        }}
        onConfirm={onMatchActivities}
        confirmTextKey="button.apply"
        maxWidth="400px">
        <p className="confirm-delete-text" style={{ whiteSpace: "pre-line" }}>
          {t("messages.confirmMatchActivities")}
        </p>
      </Dialog>
    </div>
  )
}
